import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { dimensaSubmissionFilters } from './dimensa-submission.filters';

// Manifesto de Envios Dimensa (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K. Rota sem
// menu (`showNav: false`), só no tenant raiz e com `DimensaSubmission:Read` (router.tsx:1256-1263).

export const DIMENSA_SUBMISSION_PATH = '/ccb/cessoes/submissoes-dimensa';

export const DIMENSA_SUBMISSION_READ = can('DimensaSubmission', 'Read');
/** A consulta da lista do legado exige `BatchAssignment:Read` (dimensaSubmissionContext.tsx:42), não a da rota. */
export const DIMENSA_SUBMISSION_LIST_READ = can('BatchAssignment', 'Read');

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela de envios Dimensa e selecione os lotes para reenviar.`);
};

export const dimensaSubmissionListPage = listPageAction('dimensaSubmission.list.page');

const filterKeys = dimensaSubmissionFilters.keys.join(', ');

export const dimensaSubmissionFilter = filterAction('dimensaSubmission.filter', dimensaSubmissionFilters, {
  description: msg`Filtra a lista de envios Dimensa. Filtro omitido é limpo; datas são civis (AAAA-MM-DD); status usa os códigos 0 pendente, 4 em processamento, 1 enviado, 2 rejeitado e 3 falha; fundId é o cessionário. Chaves: ${filterKeys}.`,
});

const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Sem alvo: o reenvio vale para as linhas selecionadas na lista, nunca para um id escolhido pelo agente. */
export const dimensaSubmissionResendInput = z.strictObject({
  assignmentDate: civilDate.optional(),
});
export type DimensaSubmissionResendInput = z.infer<typeof dimensaSubmissionResendInput>;

/** O texto do popup do legado (DimensaSubmissionListContainer.tsx:183-199), a mesma prévia do diálogo (U24). */
export function resendPreviewText(count: number) {
  return {
    title: t`Reenviar para a Dimensa`,
    detail: t`Confirma o reenvio de ${count} lote(s) para a Dimensa? Cessões com mais de 5 dias podem ser recusadas pela Dimensa.`,
  };
}

export const dimensaSubmissionResend = action({
  id: 'dimensaSubmission.resend',
  kind: 'write',
  label: msg`Reenviar selecionados`,
  description: msg`Reenvia para a Dimensa os lotes que a pessoa selecionou na lista, um pedido por lote, processado depois. Só entram lotes rejeitados ou com falha e sub-lotes de pendentes (número do lote terminado em letra); no sub-lote, a data de liquidação (AAAA-MM-DD) é obrigatória. Não recebe o lote: vale a seleção da tela. Pede a confirmação da pessoa.`,
  keywords: [msg`reenviar`, msg`dimensa`],
  input: dimensaSubmissionResendInput,
  preview: () => resendPreviewText(0),
  // A tela troca este `run` pelo que usa as linhas selecionadas, a mutação e o feedback dela.
  run: mountedScreenRequired,
});

const rowInput = z.strictObject({ id: z.string().min(1).max(100) });

/**
 * Intenções da linha do bloco (Detalhes e Reenviar): só da tela, fora do manifesto. O agente não recebe
 * nenhuma delas: o reenvio dele vale para as linhas selecionadas (`dimensaSubmissionResend`).
 */
export const dimensaSubmissionDetails = action({
  id: 'dimensaSubmission.details',
  kind: 'draft',
  row: true,
  label: msg`Detalhes`,
  description: msg`Abre os detalhes de um envio da lista. Nada muda só por abrir.`,
  input: rowInput,
  run: mountedScreenRequired,
});

export const dimensaSubmissionResendRow = action({
  id: 'dimensaSubmission.resendRow',
  kind: 'draft',
  row: true,
  label: msg`Reenviar`,
  description: msg`Abre a confirmação de reenvio de um envio da lista. Nada é reenviado sem confirmar.`,
  input: rowInput,
  run: mountedScreenRequired,
});

/** Seleção em lote: a tela guarda os ids e o bloco só pede para trocar (U41). */
export const dimensaSubmissionSelect = action({
  id: 'dimensaSubmission.list.select',
  kind: 'read',
  label: msg`Selecionar lotes`,
  description: msg`Troca quais linhas da lista de envios Dimensa estão selecionadas.`,
  input: z.strictObject({ ids: z.array(z.string()) }),
  run: () => undefined,
});

export const dimensaSubmissionScreen = defineScreen({
  id: 'dimensaSubmission.list',
  path: DIMENSA_SUBMISSION_PATH,
  title: msg`Envios Dimensa`,
  description: msg`Lista dos envios de cessões à Dimensa, com status, motivo do erro, tentativas e última tentativa; filtros por status, cessionário, data da cessão e data da última tentativa; a pessoa seleciona lotes rejeitados, com falha ou sub-lotes de pendentes para reenviar.`,
  permission: DIMENSA_SUBMISSION_READ,
  access: { rootTenancy: true },
  place: { icon: 'file-text', section: 'credit-note' },
  actions: [dimensaSubmissionListPage, dimensaSubmissionFilter, dimensaSubmissionResend],
});

/** Os rótulos dos filtros aplicados, pelas chaves (o fim de um período conta como o período). */
function filterLabels(keys: readonly string[]): string[] {
  const labels = dimensaSubmissionFilters.fields
    .filter((field) => keys.includes(field.key) || ('to' in field && keys.includes(field.to)))
    .map((field) => i18n._(field.label));
  return [...new Set(labels)];
}

/** O que o agente lê da tela (U24, U46 D2-C): no idioma da tela, contagem, rótulos dos filtros e seleção. */
export function dimensaSubmissionContext(input: { readonly total: number | undefined; readonly filters: readonly string[]; readonly selected: number }): ScreenContext {
  const total = input.total;
  const selected = input.selected;
  const labels = filterLabels(input.filters).join(', ');
  return {
    summary: [
      t`Envios Dimensa.`,
      total === undefined ? t`Carregando.` : t`${total} envios.`,
      labels === '' ? t`Sem filtros.` : t`Filtros: ${labels}.`,
      t`${selected} selecionados.`,
    ].join(' '),
    fields: { total: total ?? null, selected },
    sensitivity: 'internal',
  };
}
